'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import styles from './AffiliateHero.module.css';

// The site's cold-load splash (#loader, `splashFade` 800ms in styles/global.css,
// opaque until 60%) would swallow the start of the 3s orb reveal. Start the
// reveal the moment the splash begins to fade, so the blue is already spreading
// as the black lifts; at once when there is no splash (client-side navigation).
const SPLASH_FADE_START_MS = 480;

const useSplashFading = (): boolean => {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const loader = document.getElementById('loader');
    const splash = loader?.getAnimations?.().find((a) => a.playState !== 'finished');
    if (!splash) {
      setFading(true);
      return;
    }
    let raf = 0;
    const check = () => {
      const now = Number(splash.currentTime ?? 0);
      if (now >= SPLASH_FADE_START_MS || splash.playState === 'finished') {
        setFading(true);
        return;
      }
      raf = requestAnimationFrame(check);
    };
    check();
    return () => cancelAnimationFrame(raf);
  }, []);
  return fading;
};

// Blue aurora banner from the affiliate invite prototype; header/footer stay the site chrome.
export const AffiliateHero = ({ name }: { name?: string | null }) => {
  const t = useTranslations('invite');
  const invitedBy = Boolean(name && name.trim().length > 0);
  const play = useSplashFading();

  return (
    <section className={play ? `${styles.hero} ${styles.play}` : styles.hero}>
      <div className={styles.auroraWrap} aria-hidden="true">
        <div className={`${styles.aurora} ${styles.fromRight}`} />
      </div>
      <div className={`${styles.auroraWrap} ${styles.fromLeft}`} aria-hidden="true">
        <div className={`${styles.aurora} ${styles.mirror}`} />
      </div>
      <div className={styles.scrim} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.inner}>
        <h1>
          {invitedBy ? t('hero.invitedBy') : t('hero.invited')}
          {invitedBy ? (
            <>
              <br />
              <span className={styles.name}>{name}</span>
            </>
          ) : null}
        </h1>
      </div>
    </section>
  );
};
